import { useEffect, useRef } from 'react';

interface SnowflakesProps {
  isDarkMode: boolean;
}

interface Snowflake {
  x: number;
  y: number;
  size: number;
  velocityY: number;
  velocityX: number;
  opacity: number;
}

const Snowflakes = ({ isDarkMode }: SnowflakesProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const snowflakes: Snowflake[] = [];
    const numSnowflakes = 60;

    // Initialize snowflakes
    for (let i = 0; i < numSnowflakes; i++) {
      snowflakes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        velocityY: Math.random() * 1 + 0.5,
        velocityX: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.6 + 0.4,
      });
    }

    let animationId: number;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      snowflakes.forEach((flake) => {
        // Update position
        flake.y += flake.velocityY;
        flake.x += flake.velocityX;

        // Wrap around edges
        if (flake.y > height) {
          flake.y = -flake.size;
          flake.x = Math.random() * width;
        }
        if (flake.x < -flake.size) flake.x = width + flake.size;
        if (flake.x > width + flake.size) flake.x = -flake.size;

        // Draw snowflake with radial gradient for soft glow
        const gradient = ctx.createRadialGradient(
          flake.x,
          flake.y,
          0,
          flake.x,
          flake.y,
          flake.size * 2
        );
        gradient.addColorStop(0, `rgba(200, 240, 255, ${flake.opacity})`);
        gradient.addColorStop(0.5, `rgba(180, 230, 255, ${flake.opacity * 0.6})`);
        gradient.addColorStop(1, 'rgba(180, 230, 255, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.size * 2, 0, Math.PI * 2);
        ctx.fill();
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.5,
      }}
    />
  );
};

export default Snowflakes;
